import { AlertTriangle, ArrowRight, Search, X } from 'lucide-react';
import { useEffect, useReducer, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { formatDayMonth, nowISO } from '~/utils/date';

import { AttributeDisclosureCard } from './attribute-disclosure-card';
import { AttributeGroupCard } from './attribute-group-card';
import {
  enrichmentRunPanelReducer,
  INITIAL_PANEL_STATE,
  isGroupLocked,
  selectedAttributeKeys,
} from './enrichment-run-panel.reducer';
import { labelForKey as resolveLabel } from './panel-attribute-helpers';
import { QualityScoringSection } from './quality-scoring-section';
import { RetailerPromptSection } from './retailer-prompt-section';
import { TargetLocalesSection } from './target-locales-section';
import type {
  EnrichmentAttributeGroup,
  EnrichmentDisclosureGroup,
  EnrichmentRunSelection,
} from './types';

interface EnrichmentRunPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Eligible SKUs the run will cover — shown in the header and the footer summary. */
  skuCount: number;
  /** What the SKUs were scoped by (e.g. "All products"), named in the header. */
  scopeLabel: string;
  /** Normalized taxonomy. Empty while loading, erroring, or genuinely empty. */
  groups: EnrichmentAttributeGroup[];
  /**
   * Read-only fields the run always writes, rendered above `groups` with no
   * controls. Supplying this (even empty) switches the panel into disclosure mode:
   * `groups` are labelled as post-enrichment steps and Start no longer requires a
   * selection, because the run enriches every disclosed field regardless. Used by
   * Meta, whose agent has no per-field selection (agent-services #402).
   */
  disclosureGroups?: EnrichmentDisclosureGroup[];
  isLoading: boolean;
  isError: boolean;
  /** Retries the taxonomy fetch; the rest of the panel stays usable meanwhile. */
  onRetry: () => void;
  /** Request in flight — disables Start and shows its pending state. */
  isSubmitting: boolean;
  onSubmit: (selection: EnrichmentRunSelection) => void;
  /**
   * Optional scope detail rendered directly under the header — e.g. last sync and
   * average quality for the catalog this run covers. Lets a surface whose only
   * pre-run step was a scope summary drop that page entirely and open the panel
   * straight from its trigger.
   */
  scopeSummary?: ReactNode;
  /**
   * Offer the curation ingest-scope control. Off by default: only Catalog
   * Operations re-runs curation over a fixed sync, which is the case the wider
   * scope exists for. The other hosts of this panel leave the key off the payload
   * entirely, so the server's per-flow default keeps deciding for them.
   */
  showIngestMode?: boolean;
  /**
   * Net-new products in the scope (pre-flight already-curated breakdown). When it
   * is `0` and the ingest scope is "new products only", the run would have nothing
   * to enrich, so Start is blocked with a tooltip. `null`/omitted = unknown — never blocks.
   */
  netNewCount?: number | null;
  /**
   * Language/country tag of the run's scope (e.g. `en-US`), folded into the
   * suggested run name. Omitted or empty leaves the suggestion as scope + date.
   */
  localeLabel?: string;
  /**
   * Offer the google-agent retailer-prompt picker (default on). Meta hosts pass `false`:
   * meta-agent ships no retailer prompts, so the picker is hidden and `retailer` never sent.
   */
  showRetailerPrompt?: boolean;
  /**
   * Offer translation languages (default on). Meta hosts pass `false`: translation is a
   * google-agent post-step and only GMC delivers a locale, so the section is hidden and
   * `targetLocales` never sent.
   */
  showLanguages?: boolean;
  /** `data-testid` for the sheet; every control derives from it (`-name-input`, `-submit-button`, ...). */
  testId?: string;
}

/**
 * Right-anchored side panel for starting an enrichment run: run name, the
 * attribute picker driven by the config-service taxonomy, quality scoring, and a
 * sticky footer summary.
 *
 * Shared by every creation surface (Catalog Operations product selection, the
 * Product Catalog header action, Feed Audit) so the run configuration is one
 * screen with one set of rules rather than three dialogs that drift.
 *
 * Built on `Sheet` (Radix Dialog), which supplies `role="dialog"`,
 * `aria-modal`, the focus trap, focus return to the trigger, and Esc / scrim
 * dismissal. All selection logic lives in the reducer — this component renders
 * state and dispatches, and performs no dependency reasoning of its own.
 */
export function EnrichmentRunPanel({
  open,
  onOpenChange,
  skuCount,
  scopeLabel,
  groups,
  isLoading,
  isError,
  onRetry,
  isSubmitting,
  onSubmit,
  scopeSummary,
  disclosureGroups,
  showIngestMode = false,
  netNewCount = null,
  localeLabel,
  showRetailerPrompt = true,
  showLanguages = true,
  testId = 'enrichment-run-panel',
}: EnrichmentRunPanelProps) {
  // Disclosure mode: the always-on fields are the run; the selectable groups are
  // optional post-steps. Copy and the submit gate follow from that.
  const isDisclosureMode = disclosureGroups !== undefined;
  const disclosedFieldCount = (disclosureGroups ?? []).reduce(
    (sum, group) => sum + group.attributes.length,
    0,
  );
  const { t } = useTranslation('common');
  const [state, dispatch] = useReducer(enrichmentRunPanelReducer, INITIAL_PANEL_STATE);
  const [isReviewOpen, setReviewOpen] = useState(false);
  /**
   * google-agent retailer prompt slug; `''` = shared prompt. Only `@lily.ai` users see the
   * picker (it renders nothing otherwise), so for everyone else this stays `''` and is never sent.
   */
  const [retailer, setRetailer] = useState('');
  /** Locales to translate the English into; empty = English only. Internal users only, like
   *  `retailer` — the picker renders for no one else, so this stays empty and is never sent. */
  const [targetLocales, setTargetLocales] = useState<string[]>([]);
  const nameRef = useRef<HTMLInputElement>(null);

  // Load the taxonomy into panel state whenever it changes. HYDRATE no-ops on an
  // unchanged group set, so a background refetch can't wipe a live selection.
  useEffect(() => {
    if (groups.length > 0) dispatch({ type: 'HYDRATE', groups });
  }, [groups]);

  const query = state.search.trim().toLowerCase();
  const isSearching = query.length > 0;

  // Attributes are filtered by label; a group with no match disappears entirely,
  // and every surviving group force-expands so hits are never hidden behind a
  // collapsed header.
  const visibleGroups = groups
    .map((group) => ({
      group,
      visibleAttributes: isSearching
        ? group.attributes.filter((a) => a.label.toLowerCase().includes(query))
        : group.attributes,
    }))
    .filter(({ visibleAttributes }) => visibleAttributes.length > 0);

  const selectedKeys = selectedAttributeKeys(state);
  const availableKeys = groups.flatMap((g) => g.attributes.map((a) => a.key));
  const mandatoryKeys = new Set(
    groups.flatMap((g) => g.attributes.filter((a) => a.mandatory).map((a) => a.key)),
  );
  // Select all is spent once every attribute is on; Clear all is spent once only
  // mandatory attributes remain, since those can never be cleared. Unavailable
  // groups are excluded from the "all on" test — the cascade forces them off, so
  // counting them would leave Select all permanently enabled and inert.
  const selectableKeys = groups
    .filter((g) => g.unavailable !== true)
    .flatMap((g) => g.attributes.map((a) => a.key));
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => state.selected[k]);
  const hasClearableSelection = selectedKeys.some((k) => !mandatoryKeys.has(k));
  // Run name is required — the run is identified by it in the ops list, and a
  // dated auto-name is a poor default. Submit stays blocked until it's filled.
  const trimmedName = state.name.trim();
  // In disclosure mode the run has content even with every step off.
  const hasRunContent = isDisclosureMode ? disclosedFieldCount > 0 : selectedKeys.length > 0;
  // "New products only" over a scope with zero net-new products would start an
  // empty run; including already-curated products lifts the block.
  const isNothingToEnrich = showIngestMode && state.ingestMode === 'net_new' && netNewCount === 0;
  const canSubmit = hasRunContent && trimmedName.length > 0 && !isSubmitting && !isNothingToEnrich;
  const attributesCopyKey = isDisclosureMode
    ? 'enrichmentRun.postSteps'
    : 'enrichmentRun.attributes';

  // Suggested name: scope, then the locale tag when the scope has one, then today's date.
  const suggestName = () => {
    const scope = localeLabel ? `${scopeLabel} - ${localeLabel}` : scopeLabel;
    dispatch({ type: 'SET_NAME', name: `${scope} · ${formatDayMonth(nowISO())}` });
    nameRef.current?.focus();
  };

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      name: trimmedName,
      attributeKeys: selectedKeys,
      availableKeys,
      scoring: state.scoring,
      overrideCache: state.overrideCache,
      // Absent unless the surface offered the control — an omitted key means "no
      // choice recorded" and lets the server's per-flow default stand.
      ...(showIngestMode ? { ingestMode: state.ingestMode } : {}),
      // Google runs only — meta-agent has no retailer prompts, so Meta hosts hide the picker.
      ...(showRetailerPrompt && !isDisclosureMode && retailer !== '' ? { retailer } : {}),
      // Google runs only: translation is GMC content (the TRANSLATION post-step is google-agent's).
      ...(showLanguages && !isDisclosureMode && targetLocales.length > 0 ? { targetLocales } : {}),
    });
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        // Hold the panel open while the run is being created so it cannot be
        // dismissed into an unknown state mid-request.
        if (!next && isSubmitting) return;
        onOpenChange(next);
      }}
    >
      <SheetContent
        side="right"
        data-testid={testId}
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px]"
        onKeyDown={(e) => {
          // Swallow Esc while the run is being created, so the panel can't be
          // dismissed mid-request. Esc inside the search field never reaches here
          // — that handler clears the query and stops propagation first.
          if (e.key === 'Escape' && isSubmitting) e.preventDefault();
        }}
      >
        <header className="shrink-0 border-b px-6 pt-5 pb-4">
          <SheetTitle data-testid={`${testId}-title`} className="text-lg leading-8 font-semibold">
            {t('enrichmentRun.title')}
          </SheetTitle>
          <SheetDescription data-testid={`${testId}-subtitle`} className="mt-0.5 text-sm">
            {t('enrichmentRun.subtitle', { count: skuCount, scope: scopeLabel })}
          </SheetDescription>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {scopeSummary !== undefined && (
            <section data-testid={`${testId}-scope-summary`} className="border-b px-6 py-5">
              {scopeSummary}
            </section>
          )}

          {/* Run name — required; Start stays disabled until it's filled. */}
          <section className="space-y-1.5 border-b px-6 py-5">
            <Label htmlFor="enrichment-run-name" className="text-sm font-semibold">
              {t('enrichmentRun.name.label')}
              <span aria-hidden="true" className="text-destructive">
                {' *'}
              </span>
            </Label>
            <div className="relative">
              <Input
                id="enrichment-run-name"
                data-testid={`${testId}-name-input`}
                ref={nameRef}
                value={state.name}
                placeholder={t('enrichmentRun.name.placeholder')}
                disabled={isSubmitting}
                aria-required="true"
                className="pr-24"
                onChange={(e) => {
                  dispatch({ type: 'SET_NAME', name: e.target.value });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submit();
                  }
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                disabled={isSubmitting}
                className="text-accent-main hover:bg-selected-row absolute top-1 right-1.5 h-8 cursor-pointer text-xs font-semibold disabled:cursor-not-allowed"
                data-testid={`${testId}-name-suggest-button`}
                onClick={suggestName}
              >
                {t('enrichmentRun.name.suggest')}
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">{t('enrichmentRun.name.hint')}</p>
          </section>

          {/* Disclosure — what the run always writes. No controls by design. */}
          {isDisclosureMode && (
            <section
              data-testid={`${testId}-disclosure`}
              className="space-y-3.5 border-b px-6 py-5"
            >
              <div>
                <h3 className="text-foreground text-sm font-semibold">
                  {t('enrichmentRun.disclosure.title')}
                </h3>
                <p className="text-muted-foreground text-xs">
                  {t('enrichmentRun.disclosure.description')}
                </p>
              </div>
              {isError ? null : isLoading ? (
                <div
                  data-testid={`${testId}-disclosure-loading`}
                  className="space-y-2.5"
                  aria-busy="true"
                >
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-12 w-full rounded-lg" />
                  ))}
                </div>
              ) : disclosureGroups.length === 0 ? (
                <p
                  data-testid={`${testId}-disclosure-empty`}
                  className="text-muted-foreground rounded-lg border px-4 py-8 text-center text-sm"
                >
                  {t('enrichmentRun.disclosure.empty')}
                </p>
              ) : (
                <div className="space-y-2.5">
                  {disclosureGroups.map((group) => (
                    <AttributeDisclosureCard
                      key={group.slug}
                      group={group}
                      testId={`${testId}-disclosure-group-${group.slug}`}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Attributes (or, in disclosure mode, the optional post-enrichment steps) */}
          <section data-testid={`${testId}-attributes`} className="space-y-3.5 border-b px-6 py-5">
            <div>
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-foreground text-sm font-semibold">
                  {t(`${attributesCopyKey}.title`)}
                </h3>
                <div className="flex items-center gap-3">
                  <Button
                    variant="link"
                    size="sm"
                    disabled={isSubmitting || isLoading || allSelected}
                    data-testid={`${testId}-select-all-button`}
                    className="h-auto cursor-pointer p-0 text-xs font-semibold disabled:cursor-not-allowed"
                    onClick={() => {
                      dispatch({ type: 'SELECT_ALL' });
                    }}
                  >
                    {t('enrichmentRun.attributes.selectAll')}
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    disabled={isSubmitting || isLoading || !hasClearableSelection}
                    data-testid={`${testId}-clear-all-button`}
                    className="h-auto cursor-pointer p-0 text-xs font-semibold disabled:cursor-not-allowed"
                    onClick={() => {
                      dispatch({ type: 'CLEAR_ALL' });
                    }}
                  >
                    {t('enrichmentRun.attributes.clearAll')}
                  </Button>
                </div>
              </div>
              <p className="text-muted-foreground text-xs">
                {t(`${attributesCopyKey}.description`)}
              </p>
            </div>

            {isError ? (
              <div
                data-testid={`${testId}-attributes-error`}
                className="space-y-2.5 rounded-lg border px-4 py-4 text-center"
              >
                <p className="text-muted-foreground flex items-center justify-center gap-2 text-sm">
                  <AlertTriangle className="text-warning size-4 shrink-0" aria-hidden="true" />
                  {t('enrichmentRun.attributes.error')}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  data-testid={`${testId}-attributes-retry-button`}
                  onClick={onRetry}
                >
                  {t('enrichmentRun.attributes.retry')}
                </Button>
              </div>
            ) : isLoading ? (
              <div
                data-testid={`${testId}-attributes-loading`}
                className="space-y-2.5"
                aria-busy="true"
              >
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            ) : groups.length === 0 ? (
              <p
                data-testid={`${testId}-attributes-empty`}
                className="text-muted-foreground rounded-lg border px-4 py-8 text-center text-sm"
              >
                {t(`${attributesCopyKey}.empty`)}
              </p>
            ) : (
              <>
                <div className="relative">
                  <Search
                    aria-hidden="true"
                    className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
                  />
                  <Input
                    value={state.search}
                    data-testid={`${testId}-attribute-search-input`}
                    placeholder={t('enrichmentRun.attributes.searchPlaceholder')}
                    className="pr-9 pl-9"
                    onChange={(e) => {
                      dispatch({ type: 'SET_SEARCH', search: e.target.value });
                    }}
                    onKeyDown={(e) => {
                      // Clear the query rather than closing the panel, and keep the
                      // key from reaching the dialog's own Esc handler.
                      if (e.key === 'Escape' && state.search) {
                        e.stopPropagation();
                        e.preventDefault();
                        dispatch({ type: 'SET_SEARCH', search: '' });
                      }
                    }}
                  />
                  {state.search && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('enrichmentRun.attributes.clearSearch')}
                      data-testid={`${testId}-attribute-search-clear-button`}
                      className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1.5 h-7 w-7 -translate-y-1/2 cursor-pointer"
                      onClick={() => {
                        dispatch({ type: 'SET_SEARCH', search: '' });
                      }}
                    >
                      <X aria-hidden="true" className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>

                {visibleGroups.length === 0 ? (
                  <p
                    data-testid={`${testId}-attributes-no-results`}
                    className="text-muted-foreground py-8 text-center text-sm"
                  >
                    {t('enrichmentRun.attributes.noResults')}
                  </p>
                ) : (
                  <div className="space-y-2.5">
                    {visibleGroups.map(({ group, visibleAttributes }) => {
                      const locked = isGroupLocked(state, group);
                      return (
                        <AttributeGroupCard
                          key={group.slug}
                          testId={`${testId}-group-${group.slug}`}
                          group={group}
                          visibleAttributes={visibleAttributes}
                          selected={state.selected}
                          locked={locked}
                          requiresLabel={
                            group.requiresKey === undefined
                              ? null
                              : resolveLabel(groups, group.requiresKey)
                          }
                          expanded={isSearching || state.expanded[group.slug] !== false}
                          labelForKey={(key) => resolveLabel(groups, key)}
                          onToggleGroup={() => {
                            dispatch({ type: 'TOGGLE_GROUP', slug: group.slug });
                          }}
                          onToggleExpand={() => {
                            dispatch({ type: 'TOGGLE_EXPAND', slug: group.slug });
                          }}
                          onToggleAttribute={(key) => {
                            dispatch({ type: 'TOGGLE_ATTRIBUTE', key });
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </section>

          <div className="px-6 py-5">
            <QualityScoringSection
              value={state.scoring}
              disabled={isSubmitting}
              skuCount={skuCount}
              testId={`${testId}-scoring`}
              onToggle={(phase) => {
                dispatch({ type: 'TOGGLE_SCORING', phase });
              }}
            />
          </div>

          {/* Ingest scope — how much of the sync curation pulls into the run.
              Only offered where a re-run over a fixed sync is the normal case;
              elsewhere the key never reaches the payload. */}
          {showIngestMode && (
            // Border and padding sit on a wrapper: a <legend> is drawn over its fieldset's top
            // border, which struck the heading through and pushed the padding below it.
            // Floating the legend takes it out of that slot so it lays out like a heading.
            <div className="border-t px-6 py-5">
              <fieldset
                data-testid={`${testId}-ingest-mode`}
                className="min-w-0"
                disabled={isSubmitting}
              >
                <legend className="text-foreground float-left mb-1 w-full text-sm font-semibold">
                  {t('enrichmentRun.ingestMode.label')}
                </legend>
                <p className="text-muted-foreground clear-left mb-3 text-xs">
                  {t('enrichmentRun.ingestMode.description')}
                </p>
                <div className="flex flex-col gap-3">
                  {(['net_new', 'all'] as const).map((mode) => (
                    <div key={mode} className="flex items-start gap-3">
                      <input
                        type="radio"
                        id={`enrichment-run-ingest-${mode}`}
                        data-testid={`${testId}-ingest-mode-${mode}-radio`}
                        name="enrichment-run-ingest-mode"
                        value={mode}
                        checked={state.ingestMode === mode}
                        disabled={isSubmitting}
                        className="accent-primary mt-0.5 size-4 shrink-0 cursor-pointer disabled:cursor-not-allowed"
                        onChange={() => {
                          dispatch({ type: 'SET_INGEST_MODE', mode });
                        }}
                      />
                      <label
                        htmlFor={`enrichment-run-ingest-${mode}`}
                        className={`block select-none ${isSubmitting ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span className="text-foreground block text-sm font-semibold">
                          {t(`enrichmentRun.ingestMode.${mode}.label`)}
                        </span>
                        <span className="text-muted-foreground block text-xs">
                          {t(`enrichmentRun.ingestMode.${mode}.description`)}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              </fieldset>
            </div>
          )}

          {/* Retailer prompt — internal users only (self-gated), Google runs only. The agent
              still detects each product's vertical; the retailer prompt applies to the
              verticals it ships and the shared prompt covers the rest. */}
          {showRetailerPrompt && !isDisclosureMode && (
            <RetailerPromptSection
              value={retailer}
              onChange={setRetailer}
              testId={`${testId}-retailer-prompt`}
            />
          )}

          {/* Languages — internal users only (self-gated), Google runs only. English is always
              enriched; each ticked locale is translated from it as a post-step. */}
          {showLanguages && !isDisclosureMode && (
            <TargetLocalesSection
              value={targetLocales}
              onChange={setTargetLocales}
              disabled={isSubmitting}
              testId={`${testId}-target-locales`}
            />
          )}

          {/* Force generate — bypass the enrichment cache so every product is
              regenerated fresh. Off by default; opting in re-runs work that would
              otherwise be served from cache. */}
          <div className="border-t px-6 py-5">
            <div className="flex items-start gap-3">
              <Checkbox
                id="enrichment-run-override-cache"
                data-testid={`${testId}-force-generate-checkbox`}
                checked={state.overrideCache}
                disabled={isSubmitting}
                className="mt-0.5 cursor-pointer disabled:cursor-not-allowed"
                onCheckedChange={() => {
                  dispatch({ type: 'TOGGLE_OVERRIDE_CACHE' });
                }}
              />
              <label
                htmlFor="enrichment-run-override-cache"
                className={`block select-none ${isSubmitting ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className="text-foreground block text-sm font-semibold">
                  {t('enrichmentRun.forceGenerate.label')}
                </span>
                <span className="text-muted-foreground block text-xs">
                  {t('enrichmentRun.forceGenerate.description')}
                </span>
              </label>
            </div>
          </div>
        </div>

        <footer className="shrink-0 border-t px-6 py-4">
          {/* Review selection — an inline disclosure rather than a second dialog,
              so confirming what is about to run never costs the operator their
              place in the panel. */}
          {isReviewOpen && selectedKeys.length > 0 && (
            <div
              data-testid={`${testId}-review-selection`}
              className="bg-muted/40 mb-3 max-h-32 overflow-y-auto rounded-lg border p-2.5"
            >
              <div className="flex flex-wrap gap-1.5">
                {selectedKeys.map((key) => (
                  <span
                    key={key}
                    data-testid={`${testId}-review-selection-item-${key}`}
                    className="bg-selected-row text-muted-blue rounded-full px-2 py-0.5 text-xs font-medium"
                  >
                    {resolveLabel(groups, key)}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="mb-3 flex items-center justify-between gap-3">
            <p data-testid={`${testId}-footer-summary`} className="text-muted-foreground text-sm">
              {/* Disclosed fields are part of the run, so they count too. */}
              {t('enrichmentRun.footer.summary', {
                attributes: disclosedFieldCount + selectedKeys.length,
                skus: skuCount,
              })}
            </p>
            <Button
              variant="link"
              size="sm"
              disabled={selectedKeys.length === 0}
              aria-expanded={isReviewOpen}
              data-testid={`${testId}-review-selection-button`}
              className="h-auto cursor-pointer p-0 text-xs font-semibold disabled:cursor-not-allowed"
              onClick={() => {
                setReviewOpen((prev) => !prev);
              }}
            >
              {isReviewOpen
                ? t('enrichmentRun.footer.hideSelection')
                : t('enrichmentRun.footer.reviewSelection')}
            </Button>
          </div>
          <div className="flex gap-2.5">
            <Button
              variant="outline"
              disabled={isSubmitting}
              className="cursor-pointer disabled:cursor-not-allowed"
              data-testid={`${testId}-cancel-button`}
              onClick={() => {
                onOpenChange(false);
              }}
            >
              {t('enrichmentRun.footer.cancel')}
            </Button>
            {/* Disabled buttons swallow pointer events, so the tooltip hangs off a
                focusable wrapper; it only opens for the nothing-to-enrich block. */}
            <Tooltip {...(isNothingToEnrich ? {} : { open: false })}>
              <TooltipTrigger asChild>
                <span
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- lets keyboard users reach the explanation of a disabled button
                  tabIndex={isNothingToEnrich ? 0 : -1}
                  data-testid={`${testId}-submit-tooltip-trigger`}
                  className={`flex flex-1 ${canSubmit ? '' : 'cursor-not-allowed'}`}
                >
                  <Button
                    disabled={!canSubmit}
                    isLoading={isSubmitting}
                    className="flex-1 cursor-pointer gap-2 disabled:pointer-events-none disabled:cursor-not-allowed"
                    data-testid={`${testId}-submit-button`}
                    onClick={submit}
                  >
                    {t('enrichmentRun.footer.start')}
                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent data-testid={`${testId}-submit-tooltip`}>
                {t('enrichmentRun.footer.nothingToEnrich')}
              </TooltipContent>
            </Tooltip>
          </div>
        </footer>
      </SheetContent>
    </Sheet>
  );
}
